
import { useQuery } from 'react-query';

import type { IShowing } from 'src/api/landlord-showings-api';
import { apiClient } from 'src/services/api';
import { queryKeys } from 'src/services/api/constants';

const PATH = 'api/landlord/showings';

type RequestParams = {
    whole_team?: boolean;
    starts_before?: string;
    starts_after?: string;
    select?: (data: IShowing[]) => any;
    /** When false the query is disabled (e.g. lazily-loaded past tours). */
    enabled?: boolean;
};

type Response = {
    showings: IShowing[];
    has_more: boolean;
};

const toNaiveUtc = (isoDateTime: string) => isoDateTime.replace(/(?:Z|[+-]\d{2}:\d{2})$/, '');

/**
 * Showings are returned by the API sorted by start time in descending order
 * (newest first, ties broken by id descending), and resolve in that order.
 *
 * `starts_after`/`starts_before` bound the result by date. The endpoint caps
 * each call, so when `has_more` is set the hook keeps fetching with the
 * (starts_before, before_id) keyset cursor until the window is exhausted, and
 * resolves with the full window. The id tiebreaker keeps pagination exact when
 * multiple tours share a start time.
 */
export const useShowings = (params?: RequestParams) => {
    return useQuery(
        [queryKeys.showings, params?.whole_team ? 'whole_team' : ''],
        async () => {
            const showings: IShowing[] = [];
            let cursor = params?.starts_before;
            let cursorId: number | undefined;

            for (;;) {
                const searchParams = new URLSearchParams();
                if (params?.whole_team) {
                    searchParams.append('whole_team', 'true');
                }
                if (cursor) {
                    searchParams.append('starts_before', cursor);
                }
                if (cursorId !== undefined) {
                    searchParams.append('before_id', String(cursorId));
                }
                if (params?.starts_after) {
                    searchParams.append('starts_after', params.starts_after);
                }

                const response = await apiClient(PATH, {
                    searchParams,
                }).json<Response>();

                showings.push(...response.showings);

                const oldest = response.showings[response.showings.length - 1];
                if (!response.has_more || !oldest) {
                    break;
                }
                cursor = toNaiveUtc(oldest.start_time);
                cursorId = oldest.id;
            }

            return showings;
        },
        {
            select: params?.select,
            enabled: params?.enabled ?? true,
        }
    );
};
